import { Controller } from "@hotwired/stimulus"

// The reset link an admin just generated in Settings → Users. Puts it on the clipboard the moment
// the row arrives, because handing the link over is the whole point of the action — making the
// admin click a second time to get what they asked for would be the feature not doing its job.
//
// The clipboard is not guaranteed to be there: navigator.clipboard is undefined outside a secure
// context, and the write can be refused even where it exists. So the field is focused and its text
// selected on arrival regardless — that leaves the admin one keystroke from the same result, and
// it is also where focus should go after Turbo replaces the button that had it.
//
// The one thing this must never do is claim "Copied" when the write did not land.
export default class extends Controller {
  static targets = ["url", "button", "status"]
  static values = { url: String }

  static COPIED_LABEL = "Copied"
  static REST_LABEL = "Copy"
  static REVERT_DELAY = 2000

  connect() {
    this.selectUrl()
    // Focusing the field scrolls only the field into view. The block is three rows tall and a row
    // near the bottom of the scrolling settings pane would leave the hint below the fold.
    //
    // "end", not "nearest": nearest scrolls the minimum, so once the field is visible it stops and
    // leaves the hint sheared off by the pane's bottom edge — measured at 7px on the last row of a
    // nine-user list. That line carries the expiry, and in the no-clipboard case it is the only
    // thing telling the admin how to copy at all, so it is the part that must not be cut.
    this.element.scrollIntoView({ block: "end", behavior: "instant" })
    this.write()
  }

  disconnect() {
    clearTimeout(this.revertTimeout)
  }

  copy() {
    this.selectUrl()
    this.write()
  }

  write() {
    if (!navigator.clipboard?.writeText) return this.reportUnavailable()

    navigator.clipboard.writeText(this.urlValue)
      .then(() => this.reportCopied())
      .catch(() => this.reportUnavailable())
  }

  selectUrl() {
    this.urlTarget.focus()
    this.urlTarget.select()
  }

  // The button and the hint say deliberately different things, and only the button is transient.
  // The button is an action — "Copied" is momentary feedback that the press landed, and it has to
  // go back to naming what pressing it does. The hint is state: the link IS on the clipboard, and
  // stays there, so clearing that after two seconds would retract something still true.
  reportCopied() {
    this.statusTarget.textContent = "Copied to clipboard · "
    this.buttonTarget.textContent = this.constructor.COPIED_LABEL
    this.buttonTarget.classList.add("is-on")

    clearTimeout(this.revertTimeout)
    this.revertTimeout = setTimeout(() => this.reportAtRest(), this.constructor.REVERT_DELAY)
  }

  // Not an error: nothing has gone wrong that the admin can fix, and the link is right there,
  // selected. It is an instruction, so it is muted like the rest of the hint.
  reportUnavailable() {
    this.statusTarget.textContent = `Press ${this.copyShortcut()} to copy · `
    this.reportAtRest()
  }

  reportAtRest() {
    this.buttonTarget.textContent = this.constructor.REST_LABEL
    this.buttonTarget.classList.remove("is-on")
  }

  copyShortcut() {
    return /Mac|iPhone|iPad/.test(navigator.platform) ? "⌘C" : "Ctrl+C"
  }
}
